import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest} from "@angular/common/http";
import { BehaviorSubject, Observable, catchError, filter, switchMap, take, throwError } from "rxjs";
import { inject } from "@angular/core";
import { AuthService } from "../services/auth.service";
import { environment } from "../../environments/environment";
import { Router } from "@angular/router";
import { clearLocalStorageExcept } from "../app.component";


const TOKEN_HEADER_KEY = 'Authorization';

// Module-level state — shared across all requests so the refresh loop only runs once
let isRefreshing = false;
const refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

// These endpoints handle their own auth — never attach a token or retry on 401
const AUTH_ENDPOINTS = [
  'registration/ssp/login',
  'registration/ssp/verifyOtp',
  'registration/ssp/resend/otp',
];

export const AuthInterceptor: HttpInterceptorFn = (req: HttpRequest<any>, next: HttpHandlerFn) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  // Skip token injection and 401 retry for auth endpoints
  const isAuthEndpoint = AUTH_ENDPOINTS.some(ep => req.url.includes(ep));
  if (isAuthEndpoint) {
    return next(req).pipe(
      catchError((error) => {
        if (error instanceof HttpErrorResponse && error.status === 402) {
          router.navigate([''], { queryParams: { sId: localStorage.getItem('repId') } });
          clearLocalStorageExcept(['sId', 'bId', 'repId', 'storeList', 'merchants', 'countryList']);
        }
        return throwError(() => error);
      })
    );
  }

  let token: any;
  if (req.url.includes('registration/token')) {
    token = localStorage.getItem('refToken');
  } else {
    token = localStorage.getItem('token') ?? localStorage.getItem('tempTerminal');
  }

  let authReq = req;
  if (token) {
    authReq = req.clone({
      setHeaders: { Authorization: `Bearer ${token}` }
    });
  }

  return next(authReq).pipe(
    catchError((error) => {
      if (error instanceof HttpErrorResponse && error.status === 401) {
        // Don't try to refresh if the failing request was itself a refresh call
        if (req.url.includes('registration/token')) {
          isRefreshing = false;
          refreshTokenSubject.next(null);
          clearLocalStorageExcept(['sId', 'bId', 'repId', 'storeList', 'merchants', 'countryList']);
          router.navigate([''], { queryParams: { sId: localStorage.getItem('repId') } });
          return throwError(() => error);
        }

        if (!isRefreshing) {
          isRefreshing = true;
          refreshTokenSubject.next(null);

          return authService.refreshToken().pipe(
            switchMap((tokenReceived: any) => {
              isRefreshing = false;
              localStorage.setItem('token', tokenReceived.data.token);
              localStorage.setItem('refToken', tokenReceived.data.refreshToken);
              refreshTokenSubject.next(tokenReceived.data.token);

              const retryReq = req.clone({
                setHeaders: { Authorization: `Bearer ${tokenReceived.data.token}` }
              });
              return next(retryReq);
            }),
            catchError((refreshError) => {
              isRefreshing = false;
              refreshTokenSubject.next(null);
              clearLocalStorageExcept(['sId', 'bId', 'repId', 'storeList', 'merchants', 'countryList']);
              router.navigate([''], { queryParams: { sId: localStorage.getItem('repId') } });
              return throwError(() => refreshError);
            })
          );
        }

        // Another request already triggered refresh — wait for the new token
        return refreshTokenSubject.pipe(
          filter(t => t !== null),
          take(1),
          switchMap((newToken) => {
            const retryReq = req.clone({
              setHeaders: { Authorization: `Bearer ${newToken}` }
            });
            return next(retryReq);
          })
        );
      }

      if (error instanceof HttpErrorResponse && error.status === 402) {
        router.navigate([''], { queryParams: { sId: localStorage.getItem('repId') } });
        clearLocalStorageExcept(['sId', 'bId', 'repId', 'storeList', 'merchants', 'countryList']);
      }

      return throwError(() => error);
    })
  );
};
